import cn from 'classnames';
import { forwardRef } from 'react';

import { useFieldId } from '../../../utils';
import { BaseField, FieldLabel, Toggle } from '../../internal';

import type { ForwardedRef, ReactElement } from 'react';

import type { ToggleProps } from '../../internal';
import type { ToggleFieldProps } from './toggle-field.types';

import './toggle-field.scss';

/**
 * The toggle field represents an action that changes a state by clicking on it.
 * Usually used for actions or options that have an immediate effect on the UI and
 * do not need to be confirmed or reviewed.
 *
 * ### Usage
 *
 * ```tsx
 * import { ToggleField } from '@bloomreach/react-banana-ui';
 * import { useState } from 'react';
 *
 * export default function AppTheme() {
 *   const [isDark, setIsDark] = useState(false);
 *
 *   return (
 *     <ToggleField
 *       label="Dark mode"
 *       checked={isDark}
 *       onChange={() => setIsDark(!isDark)}
 *     />
 *   );
 * }
 * ```
 */
const ToggleField = forwardRef(
  (props: ToggleFieldProps, forwardedRef: ForwardedRef<HTMLInputElement> | null): ReactElement => {
    const {
      checked,
      className,
      defaultChecked,
      disabled,
      inputProps,
      label,
      name,
      onChange,
      readOnly,
      required,
      tooltip,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const controlId = useFieldId(inputProps?.id, 'rbui-toggle-field');

    const toggleProps: ToggleProps = {
      checked,
      defaultChecked,
      disabled,
      inputProps: {
        ...inputProps,
        id: controlId,
      },
      name,
      onChange,
      readOnly,
      required,
    };

    const fieldLabel = label && (
      <FieldLabel htmlFor={controlId} required={required} tooltip={tooltip} view="normal">
        {label}
      </FieldLabel>
    );

    return (
      <BaseField
        className={cn(className, 'rbui-toggle-field')}
        control={<Toggle {...toggleProps} ref={forwardedRef} />}
        label={fieldLabel}
        labelPosition="inline-end"
        {...rest}
      />
    );
  },
);

ToggleField.displayName = 'ToggleField';

export default ToggleField;
